<template>
    <v-container fluid class="pa-0">
        <ManageComponentsDialog
            :is-open="manageComponentsDialog"
            :product-components-data="calculation.calculationProducts"
            :impositions-data="impositions"
            :action-loading="actionLoading.manageComponents"
            @on-save="manageComponents"
            @on-close="closeManageComponentsDialog"
        />
        <CopyImpositionParametersDialog
            :is-open="copyImpositionParametersDialog"
            :impositions-data="impositions"
            :target-imposition-id="targetImpositionId"
            :action-loading="actionLoading.copyImpositionParameters"
            @on-save="copyImpositionParameters"
            @on-close="closeCopyImpositionParametersDialog"
        />
        <AddComponentsDialog
            :is-open="addComponentsDialog"
            :imposition-data="currentImposition"
            :product-components-data="availableCalculationProductComponents"
            :action-loading="actionLoading.addComponents"
            @on-save="addComponents"
            @on-close="closeAddComponentsDialog"
        />

        <ConfirmDialog
            :is-open="removeImpositionDialog"
            :data="currentImposition"
            :action-loading="actionLoading.removeImposition"
            @on-confirm="removeImposition"
            @on-abort="closeRemoveImpositionDialog"
        >
            <template #title>
                Usuwanie montażu
            </template>
            <template #content>
                Czy na pewno chcesz usunąć montaż? Tej operacji nie można cofnąć.
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <AddImpositionDialog
            :is-open="addImpositionDialog"
            :product-components-data="availableCalculationProductComponents"
            :action-loading="actionLoading.addImposition"
            @on-save="addImposition"
            @on-close="closeAddImpositionDialog"
        />

        <EditComponentDialog
            :is-open="editComponentDialog"
            :component-data="currentComponent"
            :action-loading="actionLoading.editComponent"
            @on-save="saveComponent"
            @on-close="closeEditComponentDialog"
        />
        <SimpleUniversalAgreeDialog
            :is-open="simpleUniversalAgreeDialog"
            :title="simpleUniversalAgreeDialogData.title"
            :description="simpleUniversalAgreeDialogData.description"
            @on-agree="closeSimpleUniversalAgreeDialog"
        />

        <ConfirmDialog
            :is-open="removeComponentDialog"
            :data="currentComponent"
            :action-loading="actionLoading.removeComponent"
            @on-confirm="removeComponent"
            @on-abort="closeRemoveComponentDialog"
        >
            <template #title>
                Odłączanie komponentu <strong>{{ currentComponent.calculationProductName }}</strong>
            </template>
            <template #content>
                Czy na pewno chcesz odłączyć komponent?
            </template>
            <template #confirmButton>
                Odłącz
            </template>
        </ConfirmDialog>

        <ConfirmDialog
            :is-open="removeEmptyImpositionsDialog"
            @on-confirm="filterOutEmptyImpositions"
            @on-abort="closeRemoveEmptyImpositionsDialog"
        >
            <template #title>
                Znaleziono puste montaże
            </template>
            <template #content>
                Czy chcesz automatycznie usunąć puste montaże?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <v-row>
            <v-col cols="12">
                <v-btn
                    v-if="isCalculationEditable"
                    color="primary"
                    outlined
                    class="mb-2"
                    data-sentry-label="Edycja zbiorcza"
                    @click="showManageComponentsDialog"
                >
                    Edycja zbiorcza
                </v-btn>
                <v-tabs
                    :value="currentTab"
                    background-color="transparent"
                    show-arrows
                    @change="changeImposition"
                >
                    <v-tabs-slider color="primary" />

                    <v-tab
                        v-for="(imposition, impositionIndex) in impositions"
                        :key="impositionIndex"
                        :class="{'error--text': !imposition.validated}"
                        @change="handleOpenedPanel"
                    >
                        Montaż {{ impositionIndex + 1 }}
                        <v-menu v-if="isCalculationEditable" offset-y>
                            <template #activator="{on}">
                                <v-btn
                                    color="grey lighten-2"
                                    fab
                                    x-small
                                    class="ml-3 elevation-0"
                                    v-on="on"
                                >
                                    <v-icon>
                                        mdi-dots-vertical
                                    </v-icon>
                                </v-btn>
                            </template>
                            <v-list dense>
                                <v-list-item
                                    :disabled="impositions.length === 1"
                                    data-sentry-label="Usuń montaż"
                                    @click="showRemoveImpositionDialog(imposition)"
                                >
                                    <v-list-item-title>Usuń montaż</v-list-item-title>
                                </v-list-item>
                                <v-list-item
                                    :disabled="impositions.length < 2"
                                    data-sentry-label="Kopiuj parametry"
                                    @click="showCopyImpositionParametersDialog(imposition)"
                                >
                                    <v-list-item-title>Kopiuj parametry</v-list-item-title>   
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </v-tab>
                    <v-tooltip v-if="isCalculationEditable" :open-delay="tooltipDelay" top>
                        <template #activator="{on}">
                            <v-btn
                                icon
                                color="primary"
                                class="align-self-center"
                                :disabled="!hasProductComponentsToAssign"
                                data-sentry-label="Dodaj montaż - modal"
                                v-on="on"
                                @click="showAddImpositionDialog"
                            >
                                <v-icon>
                                    mdi-plus
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Dodaj montaż</span>
                    </v-tooltip>
                    <v-tabs-items v-model="currentTab">
                        <v-divider />
                        <v-tab-item
                            v-for="(imposition, impositionIndex) in impositions"
                            :key="impositionIndex"
                            eager
                        >
                            <v-container fluid>
                                <v-row>
                                    <v-col cols="12" lg="4">
                                        <h2 class="mb-3 text-body-2 font-weight-medium text-uppercase">
                                            Komponenty
                                        </h2>

                                        <v-card v-for="(component) in imposition.calculationProductComponents" :key="component.id" class="mb-2">
                                            <v-container class="px-6">
                                                <v-row no-gutters align="center" class="mb-4">
                                                    <v-col v-if="isCalculationEditable" cols="auto" class="mr-2">
                                                        <v-menu offset-y>
                                                            <template #activator="{on}">
                                                                <v-btn
                                                                    color="grey lighten-2"
                                                                    fab
                                                                    x-small
                                                                    class="elevation-0"
                                                                    v-on="on"
                                                                    @click.native="$event.cancelBubble = true"
                                                                >
                                                                    <v-icon>
                                                                        mdi-dots-vertical
                                                                    </v-icon>
                                                                </v-btn>
                                                            </template>
                                                            <v-list dense>
                                                                <v-list-item
                                                                    data-sentry-label="Edytuj komponent"
                                                                    @click="showEditComponentDialog(component)"
                                                                >
                                                                    <v-list-item-title>Edytuj komponent</v-list-item-title>
                                                                </v-list-item>
                                                                <v-list-item
                                                                    data-sentry-label="Odłącz komponent"
                                                                    @click="showRemoveComponentDialog(component)"
                                                                >
                                                                    <v-list-item-title>Odłącz komponent</v-list-item-title>
                                                                </v-list-item>
                                                            </v-list>
                                                        </v-menu>
                                                    </v-col>
                                                    <v-col cols="auto">
                                                        {{ component.component.name }} - {{ component.calculationProductName }}
                                                    </v-col>
                                                </v-row>

                                                <table class="table table--simple">
                                                    <tbody>
                                                        <tr
                                                            v-for="(property) in component.calculationProductComponentProperties"
                                                            :key="property.id"
                                                        >
                                                            <th>
                                                                {{ property.productProductionProperty.verboseName | capitalize }}:
                                                            </th>
                                                            <td>
                                                                {{ parseEmptyValue(property.value) }}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <th>Ilość użytków:</th>
                                                            <td>{{ component.parts }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </v-container>
                                        </v-card>

                                        <v-tooltip v-if="isCalculationEditable" :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    class="mt-2"
                                                    block
                                                    depressed
                                                    data-sentry-label="Dodaj komponenty"
                                                    :disabled="!hasProductComponentsToAssign || !hasPrintTaskSelected(imposition) && hasTasks(imposition)"
                                                    :outlined="!hasProductComponentsToAssign || !hasPrintTaskSelected(imposition) && hasTasks(imposition)"
                                                    v-on="on"
                                                    @click="showAddComponentsDialog(imposition)"
                                                >
                                                    <v-icon
                                                        color="primary"
                                                    >
                                                        mdi-plus
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Dodaj komponent</span>
                                        </v-tooltip>
                                    </v-col>

                                    <v-col v-if="imposition.calculationProductComponents.length > 0" cols="12" lg="8">
                                        <h2 class="mb-3 text-body-2 font-weight-medium text-uppercase">
                                            Procesy
                                        </h2>

                                        <CalculationImpositionTasks
                                            ref="calculationImpositionTasks"
                                            :is-calculation-editable="isCalculationEditable"
                                            :imposition-data="imposition"
                                            :machines="machines"
                                            :papers="papers"
                                            :elastic-materials="elasticMaterials"
                                            :color-templates="colorTemplates"
                                            :calculation="calculation"
                                            @calculation-loading="toggleCalculationLoading"
                                            @is-imposition-valid="isImpositionValid"
                                            @update-calculation-paper-certificate="updateCalculationPaperCertificate"
                                        />
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-tab-item>
                    </v-tabs-items>
                </v-tabs>
            </v-col>
        </v-row>
        <v-row no-gutters justify="space-between">
            <v-col cols="auto">
                <v-btn
                    color="primary"
                    outlined
                    class="mt-3 mb-2"
                    data-sentry-label="Poprzedni krok"
                    @click="showCalculationParameters"
                >
                    Poprzedni krok
                </v-btn>
            </v-col>
            <v-col cols="auto">
                <v-btn
                    color="primary"
                    depressed
                    class="mt-3 mb-2"
                    data-sentry-label="Następny krok"
                    @click="showCalculationSummary"
                >
                    Następny krok
                </v-btn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import AddComponentsDialog from './AddComponentsDialog'
import AddImpositionDialog from './AddImpositionDialog'
import EditComponentDialog from './EditComponentDialog'
import ManageComponentsDialog from './ManageComponentsDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import CalculationImpositionTasks from './CalculationImpositionTasks/Index'
import SimpleUniversalAgreeDialog from '@/components/common/SimpleUniversalAgreeDialog'

import { appConfig } from '@/config'
import { errorHandler } from '@/utils/errorHandler'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse'

export default {
    name: 'CalculationImpositions',
    components: {
        AddComponentsDialog,
        AddImpositionDialog,
        CalculationImpositionTasks,
        ConfirmDialog,
        EditComponentDialog,
        ManageComponentsDialog,
        SimpleUniversalAgreeDialog,
        CopyImpositionParametersDialog: () => import('./CopyImpositionParametersDialog')
    },
    mixins: [ tooltipDelay, parse ],
    props: {
        isVisible: {
            type: Boolean
        },
        isCalculationEditable: {
            type: Boolean
        },
        machines: {
            type: Array,
            default: null
        },
        papers: {
            type: Array,
            default: null
        },
        colorTemplates: {
            type: Array,
            default: null
        },
        elasticMaterials: {
            type: Array,
            default: null
        }
    },
    data() {
        return {
            impositions: [],
            emptyImpositionsIds: [],
            currentImposition: {},
            currentComponent: {},
            manageComponentsDialog: false,
            copyImpositionParametersDialog: false,
            simpleUniversalAgreeDialog: false,
            removeEmptyImpositionsDialog: false, 
            addComponentsDialog: false,
            addImpositionDialog: false,
            removeImpositionDialog: false,
            editComponentDialog: false,
            removeComponentDialog: false,
            actionLoading: {
                manageComponents: false,
                copyImpositionParameters: false,
                addComponents: false,
                addImposition: false,
                removeImposition: false,
                editComponent: false,
                removeComponent: false,
            },
            componentKey: 0,
            currentTab: 0,
            isImpositionRemoved: false,
            packagesHeaders: [
                {
                    text: 'Sposób pakowania',
                    value: 'boxType',
                    align: 'left',
                    sortable: false,
                    width: 200
                },
                {
                    text: 'Rodzaj opakowania',
                    value: 'packageType',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Ilość',
                    value: 'amount',
                    align: 'center',
                    sortable: false,
                    width: 120
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 120
                },
            ],
            targetImpositionId: null,
            simpleUniversalAgreeDialogData: {
                title: '',
                description: '',
            },
        }
    },
    computed: {
        calculation() {
            return this.$store.state.calculations.calculations.calculation
        },
        canMoveForward() {
            let result = true

            if (this.availableCalculationProductComponents.length) {
                return false
            }

            this.impositions.forEach(imposition => {
                if (imposition.calculationProductComponents.length == 1) {
                    const { formatA, formatB } =
                        this.formatRangeByComponentSizeValidations.find(item => item.impositionId === imposition.id)

                    if (!(formatA && formatB)) {
                        result = false

                        return
                    }
                }
            })

            return result
        },
        availableCalculationProductComponents() {
            const result = []

            if (typeof this.calculation.calculationProducts !== 'undefined') {
                this.calculation.calculationProducts.forEach(product => {
                    product.calculationProductComponents.forEach(component => {
                        if (component.calculationImpositionId === null) {
                            result.push(component)
                        }
                    })
                })
            }

            return result
        },
        hasProductComponentsToAssign() {
            return this.availableCalculationProductComponents.length > 0
        },
        formatRangeByComponentSizeValidations() {
            const result = []

            this.impositions.forEach(imposition => {
                let minHeight = +imposition.calculationProductComponents[0].calculationProductComponentProperties.map(prop =>
                    prop.productProductionProperty.name === 'format_y' ? prop.value : false
                ).filter(prop => !!prop).at(0)
                let minWidth = +imposition.calculationProductComponents[0].calculationProductComponentProperties.map(prop =>
                    prop.productProductionProperty.name === 'format_x' ? prop.value : false
                ).filter(prop => !!prop).at(0)

                if (
                    (imposition.formatA > imposition.formatB && minHeight > minWidth) ||
                    (imposition.formatB > imposition.formatA && minWidth > minHeight)
                ) {
                    [minHeight, minWidth] = [minWidth, minHeight]
                }

                result.push({
                    impositionId: imposition.id,
                    formatA: imposition.formatA >= minWidth,
                    formatB: imposition.formatB >= minHeight
                })
            })

            return result
        }
    },
    watch: {
        isVisible(value) {
            if (value) {
                this.impositions = JSON.parse(JSON.stringify(this.calculation.calculationImpositions))
                this.currentImposition = this.impositions[0]
                this.setSharedTasksInImpositions()
            }
        }
    },
    methods: {
        updateCalculationPaperCertificate(sourceImposition) {
            this.calculation.paperCertificateId = sourceImposition.paperCertificateId
            this.calculation.paperCertificate = sourceImposition.paperCertificate
            
            this.calculation.calculationImpositions.forEach(imposition => {
                imposition.calculationImpositionTasks.forEach(task => {
                    if (task.processId === appConfig.common.processes.printTaskId) {
                        imposition.paperCertificateId = sourceImposition.paperCertificateId
                        imposition.paperCertificate = sourceImposition.paperCertificate
                    }
                })
            })
            const impositions_data = JSON.parse(JSON.stringify(this.calculation.calculationImpositions))

            this.impositions = this.impositions.map(imposition => {
                const imposition_data = impositions_data.find(
                    element => element.id === imposition.id
                )

                return {
                    ...imposition,
                    paperCertificateId: imposition_data.paperCertificateId,
                    paperCertificate: imposition_data.paperCertificate
                }
            })
        },
        async handleOpenedPanel() {
            if (!this.isImpositionRemoved) {
                await this.$nextTick(() => {
                    const impositionIndex = this.impositions.findIndex(item => item.id === this.currentImposition.id)

                    if (this.$refs.calculationImpositionTasks?.[impositionIndex]?.$refs?.panelContent?.length > 0) {
                        const openedTask = this.$refs.calculationImpositionTasks[impositionIndex].$refs.panelContent[0]

                        openedTask.handleSelectedTask()
                        this.$refs.calculationImpositionTasks[impositionIndex].closePanel()
                    }
                })
            }
        },
        hasTasks(imposition) {
            return imposition.calculationImpositionTasks.length > 0
        },
        hasPrintTaskSelected(imposition) {
            return imposition.calculationImpositionTasks && imposition.calculationImpositionTasks.some(task => {
                return task.process.id === 43 && task.isSelected
            })
        },
        isSlicingAfterPrintingTask(taskId) {
            return taskId === appConfig.common.processes.slicingAfterPrintingTaskId
        },
        isSlicingAfterFinishingTask(taskId) {
            return taskId === appConfig.common.processes.slicingAfterFinishingTaskId
        },
        setSharedTasksInImpositions() {
            const sharedTasks = this.impositions[0].calculationImpositionTasks.filter(item => item.isShared)

            this.impositions.forEach(imposition => {
                imposition.calculationImpositionTasks.forEach((task, taskIndex, calculationImpositionTasks) => {
                    sharedTasks.forEach(sharedTask => {
                        if (sharedTask.id === task.id) {
                            calculationImpositionTasks[taskIndex] = sharedTask
                        }
                    })
                })
            })
        },
        toggleCalculationLoading(value) {
            this.$emit('calculation-loading', value)
        },
        async updateCalculation(hideMainLoader = false) {
            if (!hideMainLoader) this.$emit('calculation-loading', true)

            this.impositions.forEach(imposition => {
                if (imposition.calculationImpositionDieCutters === undefined) {
                    return
                }
                let calculationImpositionDieCutters = imposition.calculationImpositionDieCutters.map(item => ({
                    ...item,
                    price: typeof item.price === 'string' ? +this.unformatPrice(item.price) : item.price
                }))

                this.$store.dispatch('calculations/updateImposition', { ...imposition, calculationImpositionDieCutters })
            })

            return await this.$store.dispatch('calculations/updateCalculation', this.calculation)
                .then(() => {
                    this.impositions = JSON.parse(JSON.stringify(this.calculation.calculationImpositions))
                    this.setSharedTasksInImpositions()
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.$emit('calculation-loading', false)
                })
        },
        changeImposition(index) {
            this.currentImposition = this.impositions[index]
            this.currentTab = index
        },
        showManageComponentsDialog() {
            this.manageComponentsDialog = true
        },
        closeManageComponentsDialog() {
            this.manageComponentsDialog = false
        },
        showCopyImpositionParametersDialog(imposition) {
            this.targetImpositionId = imposition.id
            this.copyImpositionParametersDialog = true
        },
        closeCopyImpositionParametersDialog() {
            this.copyImpositionParametersDialog = false
        },
        manageComponents(impositions) {
            this.actionLoading.manageComponents = true
            this.impositions = impositions

            this.updateCalculation(true)
                .then(() => {
                    this.closeManageComponentsDialog()
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.manageComponents = false
                })
        },
        copyImpositionParameters(sourceImpositionId, targetImpositionId) {
            this.actionLoading.copyImpositionParameters = true
            this.toggleCalculationLoading(true)

            const payload = {
                sourceImpositionId,
                destinationImpositionId: targetImpositionId
            }

            this.$store.dispatch('calculations/copyImpositionParameters', payload)
                .then(() => {
                    this.closeCopyImpositionParametersDialog()
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.$store.dispatch('calculations/getCalculation', this.calculation.id)
                        .then(() => {
                            this.impositions = JSON.parse(JSON.stringify(this.calculation.calculationImpositions))
                            this.setSharedTasksInImpositions()
                            this.toggleCalculationLoading(false)
                        })
                        .catch(error => errorHandler(error))
                    this.actionLoading.copyImpositionParameters = false
                })
        },
        showAddComponentsDialog(imposition) {
            this.currentImposition = imposition
            this.addComponentsDialog = true
        },
        closeAddComponentsDialog() {
            this.currentImposition = this.impositions[this.currentTab]
            this.addComponentsDialog = false
        },
        addComponents(imposition, calculationProductComponentIds) {
            this.actionLoading.addComponents = true

            const impositionIndex = this.impositions.findIndex(item => item.id === imposition.id)
            const currentProductComponentIds = this.impositions[impositionIndex].calculationProductComponentIds

            this.impositions[impositionIndex].calculationProductComponentIds = currentProductComponentIds.concat(calculationProductComponentIds)
            this.impositions[impositionIndex].componentsChanged = true
            this.updateCalculation(true)
                .then(() => {
                    this.closeAddComponentsDialog()
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.addComponents = false
                })
        },
        showEditComponentDialog(component) {
            this.currentComponent = component
            this.editComponentDialog = true
        },
        closeEditComponentDialog() {
            setTimeout(() => {
                this.currentComponent = {}
            }, 500)
            this.editComponentDialog = false
        },
        saveComponent(component) {
            this.actionLoading.editComponent = true


            this.$store.dispatch('calculations/setProductComponent', component)
            this.updateCalculation(true)
                .then(() => {
                    this.closeEditComponentDialog()
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.editComponent = false
                })
        },
        showRemoveComponentDialog(component) {
            this.currentComponent = component
            this.removeComponentDialog = true
        },
        closeRemoveComponentDialog() {
            this.currentImposition = this.impositions[this.currentTab]
            setTimeout(() => {
                this.currentComponent = {}
            }, 500)
            this.removeComponentDialog = false
        },
        removeComponent(component) {
            const impositionIndex = this.currentTab
            const componentIndex = this.impositions[impositionIndex].calculationProductComponentIds.findIndex(item => item === component.id)

            this.impositions[impositionIndex].calculationProductComponentIds.splice(componentIndex, 1)
            this.impositions[impositionIndex].componentsChanged = true
            this.actionLoading.removeComponent = true

            this.updateCalculation(true)
                .then(() => {
                    this.closeRemoveComponentDialog()
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.removeComponent = false
                })
        },
        showAddImpositionDialog() {
            this.addImpositionDialog = true
        },
        closeAddImpositionDialog() {
            this.addImpositionDialog = false
        },
        addImposition(imposition) {
            this.actionLoading.addImposition = true

            this.$store.dispatch('calculations/addImposition', imposition)
            this.updateCalculation(true)
                .then(() => {
                    this.closeAddImpositionDialog()
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.addImposition = false
                })
        },
        showRemoveImpositionDialog(imposition) {
            this.currentImposition = imposition
            this.removeImpositionDialog = true
        },
        closeRemoveImpositionDialog() {
            this.currentImposition = this.impositions[this.currentTab]
            this.removeImpositionDialog = false
        },
        removeImposition(imposition) {
            this.isImpositionRemoved = true
            this.actionLoading.removeImposition = true
            imposition.componentsChanged = true
            this.$store.dispatch('calculations/removeImposition', imposition.id)

            this.updateCalculation(true)
                .then(() => {
                    this.closeRemoveImpositionDialog()
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.removeImposition = false
                    this.isImpositionRemoved = false
                })
        },
        showCalculationParameters() {
            if (this.isCalculationEditable) {
                this.updateCalculation()
                    .then(() => {
                        this.$emit('show-calculation-parameters')
                        this.resetImpositionsTabs()
                    })
                    .catch(error => errorHandler(error))
            } else {
                this.$emit('show-calculation-parameters')
                this.resetImpositionsTabs()
            }
        },
        showRemoveEmptyImpositionsDialog() {
            this.removeEmptyImpositionsDialog = true
        },
        closeRemoveEmptyImpositionsDialog() {
            this.removeEmptyImpositionsDialog = false
            this.$store.dispatch('showNotification', {
                type: 'success',
                message: 'Usunięto puste montaże.'
            })

        },
        filterOutEmptyImpositions() {
            this.impositions = this.impositions.filter(imposition => 
                !this.emptyImpositionsIds.includes(imposition.id)
            )
            this.calculation.calculationImpositions = this.impositions
            this.closeRemoveEmptyImpositionsDialog()
        },
        setEmptyImpositionsIds() {
            this.emptyImpositionsIds = []

            this.impositions.forEach(imposition => {
                if (imposition.calculationProductComponents.length === 0) {
                    this.emptyImpositionsIds.push(imposition.id)
                }
            })
        },
        async showCalculationSummary() {
            this.setEmptyImpositionsIds()
            if (this.emptyImpositionsIds.length > 0) {
                this.removeEmptyImpositionsDialog = true

                return
            }
            else if (!this.canMoveForward) {
                if (this.availableCalculationProductComponents.length) {
                    this.simpleUniversalAgreeDialogData = {
                        title: 'Nieprzypisane komponenty',
                        description: 'Brak przypisanego produktu/komponentu do montażu'
                    }
                } else {
                    this.simpleUniversalAgreeDialogData = {
                        title: 'Nieprawidłowe dane',
                        description: 'Format komponentu jest mniejszy niż format arkusza'
                    }
                }
                this.simpleUniversalAgreeDialog = true

                return
            }
            if (this.isCalculationEditable) {
                await this.handleOpenedPanel()

                // setTimeout need for wait for update opened panel
                setTimeout(() => {
                    this.updateCalculation()
                        .then(() => {
                            this.resetImpositionsTabs()
                            this.$emit('show-calculation-summary')
                        })
                        .catch(error => errorHandler(error))
                }, 500)
            } else {
                this.$emit('show-calculation-summary')
                this.resetImpositionsTabs()
            }
        },
        closeSimpleUniversalAgreeDialog() {
            this.simpleUniversalAgreeDialog = false
        },
        resetImpositionsTabs() {
            this.currentImposition = {}
            this.currentTab = 0
        },
        isPaperProperty(productProductionPropertyId) {
            return productProductionPropertyId == 8
        },
        isImpositionValid(payload) {
            this.impositions.forEach((imposition, index) => {
                const calculationImpositionIndex = payload.calculationImpositions.findIndex(item => item.id === imposition.id)

                this.impositions[index].validated = payload.calculationImpositions[calculationImpositionIndex].validated

                this.impositions[index].calculationImpositionTasks.forEach(task => {
                    const calculationTaskIndex = payload.calculationImpositions[calculationImpositionIndex].calculationImpositionTasks.findIndex(taskItem =>
                        taskItem.id === task.id)

                    this.impositions[index].calculationImpositionTasks[calculationTaskIndex].validated =
                        payload.calculationImpositions[calculationImpositionIndex].calculationImpositionTasks[calculationTaskIndex].validated
                })
            })
            const validatedImpositions = this.impositions.filter(imposition => !imposition.validated)

            const isCalculationValid = validatedImpositions.length > 0

            return this.$emit('is-calculation-validated', isCalculationValid)
        }
    }
}
</script>